/**
 * When a bounded number input should commit what the merchant typed.
 *
 * 🚨 **Clamping on every keystroke makes most values unreachable.** `NumberControl` used to be a
 * controlled input whose text was `String(value)` and whose every change ran
 * `Math.min(max, Math.max(min, n))`. On a font size bounded 8–40, typing `24` clamped the first
 * keystroke `2` up to `8`, so the box read `8`; the next keystroke made `84`, clamped down to `40`.
 * Every value came out as one bound or the other — reported from a live Designer as "whatever you
 * type it's either 8 or 40".
 *
 * The rule these two encode: while the merchant is typing, only commit a number the control can
 * actually hold, and leave anything else alone so their draft survives. Clamp once, on blur, when
 * they have finished.
 *
 * Split out of the component because the admin suite runs without jsdom (see `vitest.config.ts`),
 * and because this is the part worth testing — the rest is a Nimbus input.
 */

/**
 * The value to commit while the input still has focus, or `null` to leave the current one.
 *
 * Out-of-range text is deliberately NOT clamped here: `2` on the way to `24` is a half-typed
 * number, not a request for the minimum.
 */
export function commitWhileTyping(text: string, min: number, max: number): number | null {
  if (text.trim() === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n)) return null;
  return n >= min && n <= max ? n : null;
}

/**
 * The value to commit when the input loses focus, or `null` to keep the current one.
 *
 * An emptied box keeps the previous value rather than committing `min`: `Number('')` is `0`, so
 * the old code silently turned "clear it to retype" into the minimum — a 1-character max length,
 * or an 8px font.
 */
export function commitOnBlur(text: string | null, min: number, max: number): number | null {
  if (text === null || text.trim() === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(min, n));
}
